import {
	PROPOSAL_ONLY_WEIGHT_CONCEPT_IDS,
	userCompositionColumnConcepts,
} from "$root/functional-areas/instruments/export";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import type * as smartWorkspaces from "@eu.mdotm.api/smart-workspaces";

/**
 * Snapshot selector for a bulk composition export, passed straight through to the
 * `investmentInstrumentPairsByInvestments` data provider:
 *  - `CURRENT` exports the live composition,
 *  - `PROPOSAL` the pending enhancement,
 *  - `BOTH` exports both (current and proposal side by side for investments that have a pending
 *    proposal; investments without a proposal fall back to their current composition).
 */
export type BulkCompositionExportSnapshot = "CURRENT" | "PROPOSAL" | "BOTH";

/** A predefined export concept; the parametrized-concept `uuid` is generated fresh per export. */
type CompositionExportConcept = Omit<smartWorkspaces.ParameterizedConceptDTO, "uuid">;

/**
 * Fixed leading concept — the portfolio name. Always the first column of the composition export,
 * regardless of the user's workspace column configuration; the per-instrument name, weight and
 * classification columns that follow are mirrored from the composition table itself.
 */
const PORTFOLIO_NAME_CONCEPT: CompositionExportConcept = {
	conceptId: "systemId: name",
	representationMode: "ABSOLUTE",
	label: "Portfolio",
};

/**
 * Concept id projected in the fixed leading position (the portfolio name). Excluded from the
 * workspace-preference middle so the column can never appear twice; the composition-table concept ids
 * are added to the reserved set per export by `buildBulkCompositionExportConfig`.
 */
const FIXED_CONCEPT_IDS: ReadonlySet<string> = new Set([PORTFOLIO_NAME_CONCEPT.conceptId]);

/**
 * The composition table whose persisted column preference drives the exported instrument columns:
 * the proposal composition table for proposal-flavoured exports (`PROPOSAL`/`BOTH`), the current
 * composition table otherwise. Mirrors `exportPortfolioComposition`'s enhanced/current split.
 */
function compositionColStateKeyFor(snapshot: BulkCompositionExportSnapshot): string {
	return snapshot === "CURRENT"
		? persistedColStateKeys["tableV3-investment-composition-read"]
		: persistedColStateKeys["tableV3-investment-proposalComposition-read"];
}

/**
 * The composition-table snapshot whose default all-columns layout backs the export when the user has no
 * stored composition-table preference: `PROPOSAL` for proposal-flavoured exports (`PROPOSAL`/`BOTH`,
 * which show the pre-proposal / proposed / delta weight columns), `CURRENT` otherwise.
 */
function compositionSnapshotFor(snapshot: BulkCompositionExportSnapshot): "CURRENT" | "PROPOSAL" {
	return snapshot === "CURRENT" ? "CURRENT" : "PROPOSAL";
}

/**
 * Resolves a workspace's user-preference columns into the ordered "center" concepts of the composition
 * export — the columns between the fixed portfolio-name head and the composition-table columns
 * (instrument identity, weight and classifications) that make up the tail.
 *
 * Ordering and visibility follow the user's saved grid preferences when present (`grid.preferences`,
 * hidden columns dropped), otherwise the raw `grid.projection`. Each projected UUID is resolved against
 * the workspace's own parameterized concepts plus any customer-level shared concepts (custom attributes,
 * which are stored outside the workspace DTO). Concepts whose `conceptId` is projected in a fixed
 * position (`reservedConceptIds`) are dropped so they aren't rendered twice, and an absent workspace
 * yields an empty middle.
 */
export function resolveWorkspacePreferenceConcepts(
	workspace: smartWorkspaces.SmartWorkspaceDTO | undefined,
	sharedConcepts: smartWorkspaces.ParameterizedConceptDTO[] = [],
	reservedConceptIds: ReadonlySet<string> = FIXED_CONCEPT_IDS,
): CompositionExportConcept[] {
	if (!workspace) {
		return [];
	}

	const conceptByUuid = new Map<string, smartWorkspaces.ParameterizedConceptDTO>();
	for (const concept of [...(workspace.parameterizedConcepts ?? []), ...sharedConcepts]) {
		if (concept.uuid) {
			conceptByUuid.set(concept.uuid, concept);
		}
	}

	const preferenceColumns = workspace.grid?.preferences?.data?.columns;
	const orderedUuids = preferenceColumns
		? preferenceColumns.flatMap((column) => (column.colId && !column.hide ? [column.colId] : []))
		: (workspace.grid?.projection ?? []);

	return orderedUuids.flatMap((uuid): CompositionExportConcept[] => {
		const concept = conceptByUuid.get(uuid);
		if (!concept || reservedConceptIds.has(concept.conceptId)) {
			return [];
		}
		return [concept];
	});
}

/**
 * Builds the smart-workspace data request used to bulk-export the composition of several investments
 * through the `investmentInstrumentPairsByInvestments` data provider (`exportWorkspaceData`).
 *
 * The projected concepts are `[Portfolio] + [workspace preference columns] + [composition-table
 * columns]`: a fixed leading portfolio-name column, then the user's own workspace columns (see
 * `resolveWorkspacePreferenceConcepts`), then the composition table's own columns — instrument
 * identity, weight and classifications — in the exact order and visibility the user configured for
 * that table (see `userCompositionColumnConcepts`). The proposal composition table's proposed-weight
 * and delta columns are dropped, as the bulk data provider does not serve them. This builder
 * personalizes the request per call:
 *  - projects the composition-table concepts and assigns fresh uuids to the leading portfolio/workspace
 *    columns, so each export carries a self-consistent set of identifiers;
 *  - injects the selected `investmentUuids`;
 *  - injects the chosen `snapshot` (CURRENT / PROPOSAL / BOTH);
 *  - injects `asOfDate` when the export is pinned to a date.
 */
export function buildBulkCompositionExportConfig({
	investmentUuids,
	snapshot,
	asOf,
	workspace,
	sharedConcepts,
}: {
	investmentUuids: string[];
	snapshot: BulkCompositionExportSnapshot;
	/** The day (`YYYY-MM-DD`) the compositions are taken at; `undefined` is today. */
	asOf: string | undefined;
	/** The workspace whose user-preference columns fill the center of the export (between Portfolio and the composition columns). */
	workspace?: smartWorkspaces.SmartWorkspaceDTO;
	/** Customer-level shared concepts referenced by the workspace projection but stored outside its DTO. */
	sharedConcepts?: smartWorkspaces.ParameterizedConceptDTO[];
}): smartWorkspaces.DataRequestDTO {
	// The composition-table columns (instrument identity, weight, classifications) mirror the table the
	// user sees — order and visibility; only `source` is stamped on (the back end default).
	const { concepts: compositionColumns } = userCompositionColumnConcepts(
		compositionSnapshotFor(snapshot),
		compositionColStateKeyFor(snapshot),
	);
	// The proposal composition table's proposed-weight and delta columns are not served by the bulk
	// `investmentInstrumentPairsByInvestments` provider, so drop them from proposal-flavoured exports
	// (the pre-proposal / current weight, which reuses the plain weight concept id, is kept).
	const compositionConcepts: smartWorkspaces.ParameterizedConceptDTO[] = compositionColumns
		.filter((concept) => !PROPOSAL_ONLY_WEIGHT_CONCEPT_IDS.has(concept.conceptId))
		.map((concept) => ({
			...concept,
			source: "CURRENT",
		}));

	// The workspace middle must not re-project any concept already placed by the portfolio-name head or
	// the composition-table columns, so a column can never appear twice.
	const reservedConceptIds = new Set<string>([
		...FIXED_CONCEPT_IDS,
		...compositionConcepts.map((concept) => concept.conceptId),
	]);

	const leadingConcepts: smartWorkspaces.ParameterizedConceptDTO[] = [
		PORTFOLIO_NAME_CONCEPT,
		...resolveWorkspacePreferenceConcepts(workspace, sharedConcepts, reservedConceptIds),
	].map((concept) => ({
		...concept,
		source: "CURRENT", // back end directive to always provide current source as default, snapshot property in dataProvider will have higher prio on source
		uuid: crypto.randomUUID(),
	}));

	const concepts = [...leadingConcepts, ...compositionConcepts];

	return {
		concepts,
		dataProvider: {
			dataProviderId: "investmentInstrumentPairsByInvestments",
			parameters: {
				investmentUuids: investmentUuids as unknown as object,
				snapshot: snapshot as unknown as object,
				...(asOf ? { asOfDate: asOf as unknown as object } : {}),
			},
		},
		projection: concepts.map((concept) => concept.uuid!),
		sorting: [],
	};
}
